// @flow
import type { Patient } from '@types';
import { get } from 'lodash';
import moment from 'moment';
import { shortenName, keepFirstPresent } from '@helpers/helpers';
import { STATUS_ICONS } from '../../../../elements/Calendar/status';
import { currentStatus, recurrenceSummary, servicesSummary } from './utils';

export const blockedTimeListItemsRender = (
  showRecurrent: boolean, activeModal: any, activeMenuItem: any,
  doctor: any, singleDoctor: boolean, recurrent: ?Object, isNew: boolean, isLockedForLoading: boolean, _: Function,
  savedBiweekly: boolean = false) => {
  const blockRepetition = recurrenceSummary(recurrent, _, 'label/row/recurrence/blocks', savedBiweekly);
  return [
    {
      className: 'titleField',
      customRendererData: {
        type: 'titleField',
        // disabled: isLockedForLoading,
        input: {
          icon: 'ban',
          placeholder: _('label/title'),
        },
      },
    },
    ...(showRecurrent ? [{
      className: (activeMenuItem === 'recurringSchedule') ? 'listItem active' : null,
      customRendererData: {
        action: 'recurringSchedule',
        icon: 'time',
        text: blockRepetition || _('label/row/recurrence/none_block'),
        placeholder: !blockRepetition,
        name: 'recurringSchedule',
        // hidden: !showRecurrent,
        disabled: !showRecurrent,
      // disabled: isLockedForLoading,
      },
    }] : []),
    {
      className: (activeMenuItem === 'doctor') ? 'listItem active' : 'listItem',
      customRendererData: {
        action: 'doctor',
        icon: 'doctor',
        text: doctor ? shortenName(doctor.nameWithTitle) : _('label/doctor_not_found', 'global'),
        name: 'doctor',
        disabled: !isNew || isLockedForLoading,
        forceHidden: singleDoctor,
      },
    },
  ];
};

type AppointmentLIRenderInput = {
    showRecurrent: boolean,
    activeModal: any,
    activeMenuItem: any,
    patient: ?Patient,
    isNew: boolean,
    singleRoom: boolean,
    singleDoctor: boolean,
    appointmentDoctorSelect: boolean,
    currentRoom: any,
    doctor: any,
    services: any,
    isFinatialEnabled: boolean,
    showNotes: boolean,
    isLockedForLoading: boolean,
    isWhatsAppConfirmationEnabled: boolean,
    online: boolean,
    // The status timestamps (the form's statusData, filled once the full appointment loads; until then the compact
    // appointment's badge) and the repetition, shown on their rows
    statusData: Object,
    badge: ?string,
    savedBiweekly: boolean,
    servicesByQuantity: boolean,
    recurrent: ?Object,
    _: Function
};

export const appointmentModalListItemsRender = ({
  activeMenuItem, currentRoom, doctor, appointmentDoctorSelect, isFinatialEnabled, isNew, patient,
  services, showRecurrent, singleDoctor, singleRoom, showNotes, isLockedForLoading, _, isWhatsAppConfirmationEnabled,
  online, statusData, badge, recurrent, savedBiweekly, servicesByQuantity,
} : AppointmentLIRenderInput) => {
  // Each row shows what is chosen; with nothing chosen, the field's name in grey
  const status = currentStatus(statusData, badge);
  const repetition = recurrenceSummary(recurrent, _, undefined, savedBiweekly);
  const chosenServices = servicesSummary(services, servicesByQuantity);
  return [
    {
      className: (activeMenuItem === 'patient') ? 'listItem active' : null,
      customRendererData: {
        action: 'patient',
        icon: 'patient',
        text: patient ? shortenName(patient.name) : _('label/patient', 'global'),
        phone: keepFirstPresent(patient, ['mobilePhone', 'homePhone', 'otherPhone']),
        name: 'patient',
        validate: 'patientId',
        // disabled: isLockedForLoading,
        type: 'patient',
      },
    },
    ...(showRecurrent ? [{
      className: (activeMenuItem === 'recurringSchedule') ? 'listItem active' : null,
      customRendererData: {
        action: 'recurringSchedule',
        icon: 'time',
        validate: 'recurrency',
        text: repetition || _('label/row/recurrence/none'),
        placeholder: !repetition,
        name: 'recurringSchedule',
      // disabled: isLockedForLoading,
      },
    }] : []),
    {
      className: (activeMenuItem === 'status') ? 'listItem active' : 'listItem',
      customRendererData: {
        action: 'status',
        icon: STATUS_ICONS[status.badge] || 'event_note',
        iconClassName: `status-${status.badge || 'scheduled'}`,
        text: _(`label/badge/${status.badge || 'scheduled'}`),
        detail: status.at ? moment(status.at).format(_('label/row/status_at')) : null,
        name: 'status',
        type: 'status',
        hidden: isNew,
      // disabled: isLockedForLoading,
      },
    },
    ...((isNew || !isWhatsAppConfirmationEnabled) ? [] : [
      {
        className: (activeMenuItem === 'whatsAppConfirmation') ? 'listItem active' : 'listItem',
        customRendererData: {
          action: 'whatsAppConfirmation',
          icon: 'whatsapp',
          text: _('label/whatsApp'),
          name: 'whatsAppConfirmation',
          type: 'whatsAppConfirmation',
        },
      },
    ]),
    ...(singleRoom ? [] : [
      {
        className: (activeMenuItem === 'room') ? 'listItem active' : 'listItem',
        customRendererData: {
          action: 'room',
          icon: 'location',
          text: get(currentRoom, ['name'], _('label/room', 'global')),
          name: 'room',
          forceHidden: singleRoom,
        // disabled: isLockedForLoading,
        },
      },
    ]),
    {
      className: (activeMenuItem === 'online') ? 'listItem active' : 'listItem',
      customRendererData: {
        action: 'online',
        icon: online ? 'videocam' : 'clinic',
        text: online ? _('label/online/menu_on') : _('label/online/menu_off'),
        name: 'online',
      },
    },
    ...(singleDoctor || !appointmentDoctorSelect ? [] : [
      {
        className: (activeMenuItem === 'doctor') ? 'listItem active' : 'listItem',
        customRendererData: {
          action: 'doctor',
          icon: 'doctor',
          text: doctor ? shortenName(doctor.nameWithTitle) : _('label/doctor_not_found', 'global'),
          name: 'doctor',
          disabled: !isNew || isLockedForLoading,
          forceHidden: singleDoctor,
        },
      },
    ]),
    ...(isFinatialEnabled ? [{
      className: (activeMenuItem === 'contractedServices') ? 'listItem active' : null,
      customRendererData: {
        action: 'contractedServices',
        icon: 'services',
        text: chosenServices ? chosenServices.names : _('label/contracted_services'),
        placeholder: !chosenServices,
        total: chosenServices ? chosenServices.total : null,
        name: 'contractedServices',
      // disabled: isLockedForLoading,
      },
    }] : []),
    {
      className: 'notesField',
      preventClick: true,
      customRendererData: {
        type: 'notesField',
        input: {
          icon: 'notes',
          placeholder: _('label/notes', 'global'),
        },
        checkbox: {
          label: _('label/show_obs'),
          checked: showNotes,
        },
      // disabled: isLockedForLoading,
      },
    },
  ];
};
